import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useShop } from "@/contexts/ShopContext";
import { toast } from "sonner";

export default function Kitchen() {
  const { currentShopId } = useShop();

  const [tiles, setTiles] = useState([]);
  const [category, setCategory] = useState("All");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!currentShopId) {
      setTiles([]);
      return;
    }

    try {
      const { data } = await api.get("/kitchen/queue", {
        params: { shop_id: currentShopId },
      });
      setTiles(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load kitchen queue:", error);
      toast.error(error.response?.data?.detail || "Failed to load kitchen queue");
    }
  }, [currentShopId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, [load]);

  const categories = ["All", ...new Set(tiles.map((t) => t.category))];
  const visibleTiles = category === "All" ? tiles : tiles.filter((t) => t.category === category);

  const prepareOne = async (tile) => {
    try {
      setLoading(true);
      await api.post("/kitchen/prepare-item", {
        shop_id: currentShopId,
        item_id: tile.item_id,
      });
      toast.success(`One ${tile.name} sent to delivery`);
      await load();
    } catch (error) {
      console.error("Failed to prepare item:", error?.response?.data || error);
      const detail = error?.response?.data?.detail;
      let message = "Could not mark item as prepared";
      if (typeof detail === "string") {
        message = detail;
      } else if (Array.isArray(detail)) {
        message = detail
          .map((item) => (typeof item === "string" ? item : item?.msg || JSON.stringify(item)))
          .join(", ");
      } else if (detail && typeof detail === "object") {
        message = detail.msg || detail.message || JSON.stringify(detail);
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const totalItems = tiles.reduce((sum, t) => sum + t.count, 0);

  return (
    <section className="view" data-testid="kitchen-page">
      <div className="page-head">
        <div>
          <div className="eyebrow">Pass · preparing now</div>
          <h1>Kitchen Queue</h1>
          <p>
            Each item is grouped with its total pending count across every customer. Tap an
            item to prepare one unit — the earliest order for that item is always served first.
          </p>
        </div>
        <div className="stat-note mono" data-testid="kitchen-items-count">
          {totalItems} items in progress
        </div>
      </div>

      <div className="card">
        <div className="phone-cats" style={{ padding: "14px 16px 0" }}>
          {categories.map((c) => (
            <span
              key={c}
              className={c === category ? "sel" : ""}
              onClick={() => setCategory(c)}
              data-testid={`kitchen-cat-${c}`}
            >
              {c}
            </span>
          ))}
        </div>

        <div style={{ padding: "18px", display: "flex", flexDirection: "column", gap: "10px" }}>
          {visibleTiles.map((tile) => (
            <button
              key={tile.item_id}
              className="kitchen-list-item"
              onClick={() => prepareOne(tile)}
              disabled={loading}
              data-testid={`kitchen-item-${tile.item_id}`}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "20px",
                padding: "16px 18px",
                border: "1px solid var(--border, #ddd)",
                borderRadius: "12px",
                background: "var(--surface, #fff)",
                cursor: loading ? "not-allowed" : "pointer",
                textAlign: "left",
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 800, fontSize: "16px" }}>{tile.name}</div>
                <div style={{ marginTop: "4px", fontSize: "12px", color: "var(--fg-muted)" }}>
                  {tile.category}
                </div>
              </div>

              <div style={{ textAlign: "right" }}>
                <div style={{ fontWeight: 800, fontSize: "20px" }}>{tile.count}</div>
                <div style={{ fontSize: "11px", color: "var(--fg-muted)" }}>pending</div>
              </div>

              <span className="status status-warning" style={{ whiteSpace: "nowrap" }}>
                Prepare
              </span>
            </button>
          ))}

          {visibleTiles.length === 0 && (
            <div className="empty" style={{ padding: "40px", textAlign: "center" }}>
              Kitchen is clear — no active Food or Drinks
            </div>
          )}
        </div>

        <div className="note">
          Each tap prepares exactly one unit, taken from the earliest order that still needs
          this item — regardless of which customer placed it. That unit moves to Delivery
          immediately. An item ordered multiple times by the same customer goes to Delivery
          one unit at a time, same as separate customers.
        </div>
      </div>
    </section>
  );
}